
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

export const addTaskToMyTasks = async (taskId: string, taskSource: 'admin' | 'school', toast: ReturnType<typeof useToast>['toast']) => {
  try {
    console.log('addTaskToMyTasks called with:', { taskId, taskSource })
    
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      console.error('No session found')
      toast({
        title: "Error",
        description: "No session found",
        variant: "destructive",
      })
      return false
    }

    console.log('Session found for user:', session.user.id)

    // For admin tasks, we need to insert into user_tasks differently
    // since admin_tasks don't have a direct relationship with user_tasks
    if (taskSource === 'admin') {
      console.log('Processing admin task...')
      
      // First, get the admin task details
      const { data: adminTask, error: fetchError } = await supabase
        .from('admin_tasks')
        .select('*')
        .eq('id', taskId)
        .single()

      if (fetchError) {
        console.error('Error fetching admin task:', fetchError)
        throw fetchError
      }

      console.log('Admin task fetched:', adminTask)

      // Create a custom task based on the admin task
      const customTaskData = {
        user_id: session.user.id,
        title: adminTask.title,
        description: adminTask.description,
        module: adminTask.module,
        domain: adminTask.domain,
        status: 'active'
      }

      console.log('Inserting custom task with data:', customTaskData)

      const { error } = await supabase
        .from('custom_tasks')
        .insert(customTaskData)

      if (error) {
        console.error('Error inserting custom task:', error)
        throw error
      }

      console.log('Custom task inserted successfully')
    } else {
      console.log('Processing school task...')
      
      // For school tasks, use the existing logic
      const { error } = await supabase
        .from('user_tasks')
        .insert({
          user_id: session.user.id,
          task_id: taskId,
          status: 'active'
        })

      if (error) {
        console.error('Error inserting user task:', error)
        throw error
      }

      console.log('User task inserted successfully')
    }

    toast({ title: "Task added to My Tasks" })
    return true
  } catch (error: any) {
    console.error('Error adding task:', error)
    toast({
      title: "Error",
      description: `Failed to add task: ${error.message}`,
      variant: "destructive",
    })
    return false
  }
}

export const updateTaskStatus = async (
  taskId: string, 
  status: string,
  isCustomTask = false,
  toast: ReturnType<typeof useToast>['toast']
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return false

    if (isCustomTask) {
      const { error } = await supabase
        .from('custom_tasks')
        .update({ status })
        .eq('id', taskId)
        .eq('user_id', session.user.id)

      if (error) throw error
    } else {
      const { error } = await supabase
        .from('user_tasks')
        .update({ status })
        .eq('id', taskId)
        .eq('user_id', session.user.id)

      if (error) throw error
    }

    toast({ title: "Task status updated" })
    return true
  } catch (error: any) {
    console.error('Error updating task status:', error)
    toast({
      title: "Error",
      description: "Failed to update task status",
      variant: "destructive",
    })
    return false
  }
}
